import React, { useState } from "react";

const INITIAL_STATE = {
  name: "",
  phone: "",
  email: "",
  subject: "",
  message: "",
};

// wa.me needs digits only, with country code (91 = India)
const WA_NUMBER = "918714514035";

const buildWhatsAppUrl = (f) => {
  const lines = [
    "*New enquiry from the website*",
    "",
    `*Name:* ${f.name.trim()}`,
    `*Phone:* ${f.phone.trim()}`,
    ...(f.email.trim() ? [`*Email:* ${f.email.trim()}`] : []),
    ...(f.subject.trim() ? [`*Subject:* ${f.subject.trim()}`] : []),
    "",
    "*Message:*",
    f.message.trim(),
  ];
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
};

export default function ContactForm() {
  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [waUrl, setWaUrl] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name";
    if (!form.phone.trim()) {
      next.phone = "Please enter your phone number";
    } else if (form.phone.replace(/\D/g, "").length < 10) {
      next.phone = "Enter a valid phone number";
    }
    // Email is optional, but must be valid if filled in
    if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email)) {
      next.email = "Enter a valid email address";
    }
    if (!form.message.trim()) next.message = "Please add a short message";
    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const url = buildWhatsAppUrl(form);

    // Must run inside the click handler so browsers don't block it.
    // If the popup is blocked, open WhatsApp in this tab instead.
    const win = window.open(url, "_blank");
    if (win) {
      win.opener = null;
    } else {
      window.location.href = url;
    }

    setWaUrl(url);
    setForm(INITIAL_STATE);
  };

  if (waUrl) {
    return (
      <div className="contact-form__success">
        <svg
          width="52"
          height="52"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="11"
            stroke="var(--color-accent)"
            strokeWidth="1.5"
          />
          <path
            d="M7 12.5l3 3 7-7"
            stroke="var(--color-accent)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <h3>Almost done, just press Send in WhatsApp</h3>
        <p>
          We've opened WhatsApp with your message ready. Tap{" "}
          <strong>Send</strong> there and we'll reply as soon as we can.
        </p>
        <div className="btn-row" style={{ justifyContent: "center" }}>
          <a className="btn" href={waUrl} target="_blank" rel="noreferrer">
            Open WhatsApp Again
          </a>
          <button
            type="button"
            className="btn btn--outline"
            onClick={() => setWaUrl("")}
          >
            Send another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <div className="contact-form__row">
        <div className="contact-form__field">
          <label htmlFor="name">Name</label>
          <input
            id="name"
            name="name"
            type="text"
            value={form.name}
            onChange={handleChange}
            placeholder="Your full name"
          />
          {errors.name && (
            <span className="contact-form__error">{errors.name}</span>
          )}
        </div>
        <div className="contact-form__field">
          <label htmlFor="phone">Phone</label>
          <input
            id="phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            placeholder="+91 00000 00000"
          />
          {errors.phone && (
            <span className="contact-form__error">{errors.phone}</span>
          )}
        </div>
      </div>

      <div className="contact-form__field">
        <label htmlFor="email">
          Email <small>(optional)</small>
        </label>
        <input
          id="email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          placeholder="you@example.com"
        />
        {errors.email && (
          <span className="contact-form__error">{errors.email}</span>
        )}
      </div>

      <div className="contact-form__field">
        <label htmlFor="subject">Subject</label>
        <input
          id="subject"
          name="subject"
          type="text"
          value={form.subject}
          onChange={handleChange}
          placeholder="What are you looking for?"
        />
      </div>

      <div className="contact-form__field">
        <label htmlFor="message">Message</label>
        <textarea
          id="message"
          name="message"
          rows="5"
          value={form.message}
          onChange={handleChange}
          placeholder="Tell us about the furniture you need, room size, and preferred wood."
        />
        {errors.message && (
          <span className="contact-form__error">{errors.message}</span>
        )}
      </div>

      <button type="submit" className="btn">
        Send via WhatsApp
      </button>
    </form>
  );
}
